'use client';

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { bindPreviewDialog, bindPreviewFrame } from '@/lib/widgets/preview-events';
import type { LucideIcon } from 'lucide-react';
import { embeddedSitesLine } from '@/lib/embed/adoption';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeftRight,
  ArrowRightLeft,
  Battery,
  Beef,
  Beer,
  Building2,
  Calendar,
  Calculator,
  Check,
  CheckCircle,
  Clock,
  Code,
  Copy,
  Cpu,
  CreditCard,
  DollarSign,
  Droplet,
  Eye,
  Gift,
  Globe,
  GraduationCap,
  Grid,
  Home,
  IceCream,
  Info,
  Layers,
  LayoutGrid,
  Leaf,
  Lock,
  Newspaper,
  Package,
  Receipt,
  Ruler,
  Scale,
  Search,
  Shield,
  ShieldCheck,
  Smartphone,
  Snowflake,
  Sparkles,
  Star,
  Sun,
  Thermometer,
  TrendingUp,
  Truck,
  Utensils,
  Wine,
  Wrench,
  Zap,
} from 'lucide-react';
import {
  ConsiderationCard,
  NumberedStepList,
  SectionRule,
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from '@/components/brand-kit';
import {
  resolveWidgetIconName,
  type WidgetIconName,
  type WidgetIconSource,
} from '@/lib/widgets/icon-resolver';
import { staticPageBreadcrumbs } from '@/lib/breadcrumbs';
import type { WidgetTool } from './page';
import { useIsHydrated } from '@/hooks/use-is-hydrated'

interface WidgetConfig {
  id: string;
  containerId: string;
  scriptSrc: string;
  title: string;
  description: string;
  toolPath: string;
  iconName: WidgetIconName;
  iconSource: WidgetIconSource;
}

const ICON_COMPONENTS: Record<WidgetIconName, LucideIcon> = {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeftRight,
  ArrowRightLeft,
  Battery,
  Beef,
  Beer,
  Calendar,
  Calculator,
  Check,
  CheckCircle,
  ChefHat: Utensils,
  Clock,
  Cpu,
  CreditCard,
  DollarSign,
  Droplet,
  Eye,
  Gift,
  Globe,
  Grid,
  Home,
  IceCream,
  Info,
  Layers,
  LayoutGrid,
  Leaf,
  Lock,
  Package,
  Receipt,
  Ruler,
  Scale,
  Search,
  Shield,
  ShieldCheck,
  Smartphone,
  Snowflake,
  Sparkles,
  Star,
  Sun,
  Thermometer,
  TrendingUp,
  Truck,
  Utensils,
  Wine,
  Wrench,
  Zap,
};

function widgetsToCategories(widgets: WidgetTool[]): { name: string; widgets: WidgetConfig[] }[] {
  const configs: WidgetConfig[] = widgets.map((t) => {
    const resolution = resolveWidgetIconName(t);
    return {
      id: t.slug,
      containerId: 'fridge-' + t.widget_script.replace(/\.js$/, ''),
      scriptSrc: '/widgets/' + t.widget_script,
      title: t.name,
      description: t.description || '',
      toolPath: '/tools/' + t.slug,
      iconName: resolution.iconName,
      iconSource: resolution.source,
    };
  });
  const byCategory = new Map<string, WidgetConfig[]>();
  widgets.forEach((t, i) => {
    const category = t.category || 'Other';
    if (!byCategory.has(category)) byCategory.set(category, []);
    byCategory.get(category)!.push(configs[i]);
  });
  return Array.from(byCategory.entries()).map(([name, w]) => ({ name, widgets: w }));
}

/**
 * WidgetRowHeader — collapsed-card click target for a single embeddable widget.
 * Always rendered. The expanded preview + embed-code panel renders separately
 * below the entire 2-column row so the grid stays balanced.
 */
function WidgetRowHeader({
  widget,
  isOpen,
  onToggle,
}: {
  widget: WidgetConfig;
  isOpen: boolean;
  onToggle: (trigger: HTMLButtonElement) => void;
}) {
  const Icon = ICON_COMPONENTS[widget.iconName];
  return (
    <div
      data-widget-card={widget.id}
      data-widget-icon={widget.iconName}
      data-widget-icon-source={widget.iconSource}
      className={`bg-white overflow-hidden transition-colors rounded border ${
        isOpen ? 'border-gray-900' : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        data-widget-toggle={widget.id}
        onClick={(event) => onToggle(event.currentTarget)}
        className="w-full px-[18px] py-4 flex items-center gap-[14px] text-left hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="w-10 h-10 bg-[#ff9951]/10 flex items-center justify-center text-[#ff9951] shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-gray-900 text-[14.5px] tracking-[-0.005em]">
            {widget.title}
          </div>
          {/* Two lines, not one: every description was cut to a single line at 390 (FRIDGE-1031). */}
          <div className="text-[12.5px] text-gray-500 line-clamp-2 mt-0.5">{widget.description}</div>
        </div>
        <div
          aria-hidden
          className="shrink-0 inline-flex items-center justify-center border border-gray-200 px-[10px] py-1 font-mono text-[10px] font-bold tracking-[0.08em] text-gray-500"
        >
          PREVIEW
        </div>
      </button>
    </div>
  );
}

/**
 * WidgetPreviewModal — centered modal with preview + embed-code for the open
 * widget. Replaces the previous inline-expansion pattern that pushed the
 * neighbouring row card down. Backdrop blur, click-outside / ESC / X to
 * dismiss. Loads the v2 runtime so the new editorial design is what the
 * customer sees in the catalog.
 */
function WidgetPreviewModal({
  widget,
  openerRef,
  onClose,
}: {
  widget: WidgetConfig;
  openerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const mounted = useIsHydrated();

  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const [previewHeight, setPreviewHeight] = useState(420);

  // The browser owns modal focus, inert background content and the top layer.
  // Closing restores focus to the widget button that opened the preview.
  useEffect(() => {
    const dialog = dialogRef.current;
    const opener = openerRef.current;
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    closeButtonRef.current?.focus({ preventScroll: true });
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      // Restore after React removes the dialog; focusing during its unmount
      // can be reset to body by the browser's modal cleanup.
      requestAnimationFrame(() => {
        if (!dialog.open && opener?.isConnected) opener.focus({ preventScroll: true });
      });
    };
  }, [openerRef]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    return bindPreviewDialog(dialog, closeButtonRef.current, copyButtonRef.current, onClose);
  }, [onClose]);

  const previewFrameRef = useCallback((frame: HTMLIFrameElement | null) => {
    if (frame) return bindPreviewFrame(frame, setPreviewHeight, onClose);
  }, [onClose]);

  const v2ScriptPath = `/widgets/_v2/${widget.scriptSrc.replace(/^\/widgets\//, '')}`;
  const scriptOrigin =
    mounted && typeof window !== 'undefined' ? window.location.origin : 'https://fridge.com';
  const v2ScriptUrl = scriptOrigin + v2ScriptPath;
  const v2AtomsUrl = scriptOrigin + '/widgets/_v2/_atoms.js';
  const v2CssUrl = scriptOrigin + '/widgets/_v2/_design.css';

  // Embed code shown to customers — points at the v2 runtime on production.
  const embedCode =
    `<link rel="stylesheet" href="https://fridge.com/widgets/_v2/_design.css">\n` +
    `<div id="${widget.containerId}"></div>\n` +
    `<script src="https://fridge.com/widgets/_v2/_atoms.js"></script>\n` +
    `<script src="https://fridge.com/widgets/_v2${widget.scriptSrc.replace(/^\/widgets/, '')}"></script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-[550px] max-h-[calc(100dvh-2rem)] overflow-visible border-0 bg-transparent p-0 text-gray-900 backdrop:bg-white/80 backdrop:backdrop-blur-sm"
    >
      <div className="flex max-h-[calc(100dvh-2rem)] min-w-0 flex-col">
        <div className="flex shrink-0 justify-end mb-2">
          <button
            type="button"
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 inline-flex items-center justify-center bg-white border border-gray-900 text-gray-900 text-xl leading-none rounded-full shadow-md hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0469f0]"
          >
            ×
          </button>
        </div>

        {/* Modal panel */}
        <div className="bg-white border border-gray-900 rounded shadow-2xl overflow-hidden flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#ff9951]">
              Live preview
            </div>
            <h2 id={titleId} className="font-extrabold text-gray-900 text-[15px]">{widget.title} preview</h2>
          </div>
        </div>

        {/* Scrollable body — preview + embed code. flex-1/min-h-0 so the
            inner overflow scrolls when the panel hits its max-h cap. */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {/* Preview */}
          <div className="p-2 sm:p-5 bg-gray-50 flex items-center justify-center border-b border-gray-200">
            {mounted ? (
              <iframe
                data-widget-preview={widget.id}
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base href="${scriptOrigin}/"><link rel="stylesheet" href="${v2CssUrl}" data-fridge-widget-css><style>html,body{margin:0;padding:0}body{padding:12px;display:flex;justify-content:center;background:transparent;font-family:system-ui,-apple-system,sans-serif}</style></head><body><div id="${widget.containerId}"></div><script src="${v2AtomsUrl}"></script><script src="${v2ScriptUrl}"></script></body></html>`}
                className="w-full border-0"
                style={{ height: previewHeight, maxWidth: '480px' }}
                ref={previewFrameRef}
                title={`${widget.title} Preview`}
                sandbox="allow-scripts allow-same-origin"
              />
            ) : (
              <div
                className="w-full bg-gray-50"
                style={{ height: '420px', maxWidth: '480px' }}
                aria-hidden
              />
            )}
          </div>

          {/* Embed code */}
          <div className="p-4 sm:p-5 bg-white flex min-w-0 flex-col">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-gray-500">
                <Code className="w-3.5 h-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em]">
                  Embed Code
                </span>
              </div>
              <button
                type="button"
                ref={copyButtonRef}
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] hover:bg-[#ff8533] transition-colors font-bold"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" /> Copy
                  </>
                )}
              </button>
            </div>
            <pre className="text-gray-900 text-xs overflow-x-auto whitespace-pre-wrap break-all leading-relaxed font-mono m-0">
              {embedCode}
            </pre>
            <div className="mt-3 pt-2 border-t border-gray-200 flex flex-wrap gap-3 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-600">
              <span>4 lines</span>
              <span>Shared CSS + JavaScript</span>
            </div>
          </div>
        </div>
        </div>
      </div>
    </dialog>
  );
}

const FAQ_ITEMS = [
  {
    question: 'Are the widgets really free?',
    answer:
      'Yes, completely free. No registration, no API key, no limits. The only requirement is keeping the "Powered by Fridge.com" attribution visible.',
  },
  {
    question: 'Can I customize the styling?',
    answer:
      'The widgets are designed to look clean on any site. The source code cannot be modified to remove attribution, but the widgets adapt to most layouts within their max-width of 420px.',
  },
  {
    question: 'Do widgets slow down my site?',
    answer:
      "The embed loads a shared stylesheet, shared JavaScript and the selected widget script from Fridge.com. Download size varies by widget and browser caching. Test the embed on your page; the supplied script tags do not use async or defer.",
  },
  {
    question: 'How do I update the widget?',
    answer:
      "You don't need to. Widgets load from fridge.com each time, so you always get the latest version. Just keep the embed code on your page.",
  },
  {
    question: 'What does the attribution requirement mean?',
    answer:
      'Each widget includes a small "Powered by Fridge.com" link at the bottom. This must remain visible and unmodified. It\'s how we keep the widgets free.',
  },
  {
    question: 'Can I use these on a commercial site?',
    answer:
      'Yes. Commercial, personal, educational, government — any website. The only restriction is keeping the attribution link.',
  },
];

interface WidgetsClientProps {
  widgets: WidgetTool[];
  /** FRIDGE-807: sites embedding a widget over the trailing window; null = not measured. */
  embeddingSites?: number | null;
}

export default function WidgetsClient({ widgets, embeddingSites = null }: WidgetsClientProps) {
  const categories = useMemo(() => widgetsToCategories(widgets), [widgets]);
  const embeddedLine = embeddedSitesLine(embeddingSites);
  const [openWidgetId, setOpenWidgetId] = useState<string | null>(null);
  const widgetOpenerRef = useRef<HTMLButtonElement | null>(null);
  const count = widgets.length;
  const openWidget = useMemo(() => {
    if (!openWidgetId) return null;
    for (const c of categories) {
      const found = c.widgets.find((w) => w.id === openWidgetId);
      if (found) return found;
    }
    return null;
  }, [openWidgetId, categories]);

  return (
    <ToolPageShell
      // ONE trail (lib/breadcrumbs.ts): the shell renders it visibly AND as the
      // BreadcrumbList JSON-LD (FRIDGE-269) — page.tsx no longer hand-types a
      // second list.
      trail={staticPageBreadcrumbs({ name: 'Embeddable Widgets' })}
      heroEyebrow="Embeddable Widgets"
      heroTitlePre="Free"
      heroTitleItalic="Embeddable"
      heroTitlePost="Calculators"
      heroSubtitle="Add a refrigerator or freezer calculator to your site. Preview a widget, copy four lines of HTML and keep the Fridge.com attribution visible."
      heroMeta={['Live previews', 'Copy-paste HTML', 'Free to embed', ...(embeddedLine ? [embeddedLine] : [])]}
      heroShare={<ToolShareBar title="Embeddable Widgets" />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: String(count), label: 'Free Tools' },
            { value: '4', label: 'Lines of HTML' },
            { value: 'CSS + JS', label: 'Shared runtime' },
            { value: 'Free', label: 'With attribution' },
          ]}
        />
      }
      faqs={FAQ_ITEMS}
      faqEyebrow="Questions"
      faqTitle="Frequently asked"
      cta={{
        title: 'Have a question?',
        body: 'Questions about widgets, embedding, or anything else? The team at Fridge.com is here to help.',
        buttonLabel: 'Contact Us',
        buttonHref: '/contact',
      }}
    >
      <main id="main-content" className="bg-white">
        <div className="max-w-[1320px] mx-auto px-6 md:px-8 py-10 md:py-12">
          {/* How It Works */}
          <section>
            <SectionRule eyebrow="How it works" title="How It Works" />
            <NumberedStepList
              showArrow={false}
              steps={[
                {
                  number: '01',
                  title: 'Pick a calculator',
                  description: `Scan the ${count} tools below by category — energy and cost, sizing, food storage, beverages, maintenance, buying. Click any tile to see exactly how it will look on your page.`,
                },
                {
                  number: '02',
                  title: 'Copy four lines of HTML',
                  description: 'Hit the orange Copy button next to the embed code. The snippet loads the shared styles, shared runtime and selected widget script — no account or API key required.',
                },
                {
                  number: '03',
                  title: 'Paste it into your page',
                  description: 'Paste the snippet into an HTML block that permits styles and scripts. Preview the result on desktop and mobile, and keep the Fridge.com attribution visible.',
                },
              ]}
            />
          </section>

          {/* Catalog */}
          <section className="mt-12 md:mt-16">
            <SectionRule
              eyebrow="The catalog"
              title="Every widget, by category"
              right={`${categories.length} categories · ${count} tools`}
            />
            {categories.length === 0 ? (
              <p className="text-center text-gray-500">
                No widgets available. Widget data is loaded from the database.
              </p>
            ) : (
              <div className="space-y-12">
                {categories.map((category, ci) => (
                  <div key={category.name}>
                    {ci === 1 && (
                      <div className="mb-12 -mx-2">
                        <ToolProductCarousel
                          eyebrow="Trending this week"
                          title="Most-shopped refrigerators"
                          description="The fridges customers are bringing home alongside these widgets."
                          collectionType="french-door"
                          fallbackCollectionType="counter-depth"
                        />
                      </div>
                    )}
                    {ci === Math.floor(categories.length / 2) && (
                      <div className="mb-12 -mx-2">
                        <ToolProductCarousel
                          eyebrow="Best value"
                          title="Counter-depth picks with strong efficiency ratings"
                          description="Pair the size + energy widgets above with one of these picks."
                          collectionType="counter-depth"
                          fallbackCollectionType="top-freezer"
                          randomize
                        />
                      </div>
                    )}
                    <div className="flex items-baseline gap-4 mb-[18px]">
                      <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951]">
                        № {String(ci + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-[22px] font-black tracking-[-0.02em] text-gray-900 m-0">
                        {category.name}
                      </h3>
                      <div
                        aria-hidden
                        className="flex-1 h-px bg-gray-200 relative -top-1.5"
                      />
                      <span className="text-xs font-bold tracking-[0.05em] uppercase text-gray-500">
                        {category.widgets.length} {category.widgets.length === 1 ? 'tool' : 'tools'}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      {category.widgets.map((widget) => (
                        <WidgetRowHeader
                          key={widget.id}
                          widget={widget}
                          isOpen={openWidgetId === widget.id}
                          onToggle={(trigger) => { widgetOpenerRef.current = trigger; setOpenWidgetId(widget.id); }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Use Cases */}
          <section className="mt-12 md:mt-16">
            <SectionRule eyebrow="Who uses these" title="Who Uses These" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <ConsiderationCard
                icon={<Building2 className="w-5 h-5 text-[#0469f0]" />}
                title="Real Estate Agents"
                description="Energy cost calculator on property listings"
              />
              <ConsiderationCard
                icon={<Newspaper className="w-5 h-5 text-[#0469f0]" />}
                title="Home Improvement Bloggers"
                description="Size and efficiency calculators in buying guides"
              />
              <ConsiderationCard
                icon={<Zap className="w-5 h-5 text-[#0469f0]" />}
                title="Utility Companies"
                description="Energy tools on customer resource pages"
              />
              <ConsiderationCard
                icon={<GraduationCap className="w-5 h-5 text-[#0469f0]" />}
                title="Universities"
                description="Food storage guide for student resources"
              />
            </div>
          </section>

          {/* Inline carousel #3 — between Use Cases and FAQ */}
          <section className="mt-12 md:mt-16">
            <ToolProductCarousel
              eyebrow="Specialty"
              title="Wine fridges and beverage centers"
              description="Drop the wine-zone or kegerator-yield widgets next to one of these on your listing."
              collectionType="wine-cooler"
              fallbackCollectionType="side-by-side"
              randomize
            />
          </section>
        </div>
      </main>

      {openWidget && (
        <WidgetPreviewModal widget={openWidget} openerRef={widgetOpenerRef} onClose={() => setOpenWidgetId(null)} />
      )}
    </ToolPageShell>
  );
}
